/**
 * Blog editor canvas selection — read what the operator has highlighted.
 *
 * A read, so an agent can quote or reason about the selected passage before it
 * suggests an edit. `consequence: 'reads'`: it changes nothing, skips the dirty
 * veto, and returns the selection's plain text plus its current mark state.
 */
import { z } from 'zod';
import {
  defineCommand,
  refuse,
  type CommitAuthorization,
  type Diff,
  type EditorContext,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';

const noArgs = z.object({}).describe('No arguments; reports the current selection.');
type NoArgs = z.infer<typeof noArgs>;

/** Kept short so the whole receipt stays well under the output ceiling. */
const MAX_QUOTED = 600;

export const edReadSelection = defineCommand<NoArgs, EditorContext>({
  name: 'ed_read_selection',
  section: 'editor',
  consequence: 'reads',
  description:
    'Read the text the operator has selected in the blog editor, and whether bold or italic is active on it. Returns an empty selection when the cursor is collapsed. Use it before suggesting an edit to the highlighted passage.',
  args: noArgs,

  async resolve(input: ResolveInput<NoArgs>): Promise<EditorContext | Refusal> {
    const { tenant, read } = input;
    if (!read.editorContext) {
      return refuse('This surface cannot see the blog editor.');
    }
    const ctx = read.editorContext(tenant);
    if (!ctx) {
      return refuse('No blog editor is open in this tab.', { retryable: true });
    }
    return ctx;
  },

  /** A read changes nothing; the empty change list is the honest diff. */
  diff(target: EditorContext, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      changes: [],
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: EditorContext,
    _auth: CommitAuthorization,
    _write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    if (target.selectionEmpty) {
      return {
        summary: 'Nothing is selected in the editor right now — the cursor is collapsed.',
        facts: { selectionEmpty: true, boldActive: target.boldActive, italicActive: target.italicActive },
      };
    }
    const quoted = target.selectedText.slice(0, MAX_QUOTED);
    const truncated = target.selectedText.length > MAX_QUOTED;
    const marks = [target.boldActive ? 'bold' : null, target.italicActive ? 'italic' : null]
      .filter(Boolean)
      .join(', ');
    return {
      summary:
        `Selected text${marks ? ` (${marks})` : ''}: "${quoted}"${truncated ? '… (truncated)' : ''}`,
      facts: {
        chars: target.selectedText.length,
        boldActive: target.boldActive,
        italicActive: target.italicActive,
        truncated,
      },
    };
  },
});
